import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Calendar } from 'lucide-react'

import {
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { Location } from '../../types/response'
import { paths } from '@/utils/path'
import { LOCATION_TYPE } from '../../constants'

dayjs.extend(relativeTime)

// === Columns ===

export const getLocationColumns = (): DataTableColumn<Location>[] => [
    {
        key: 'name',
        title: 'Name',
        width: 250,
        pinned: 'left',
        render: (value, row) => (
            <Link
                to={`${paths.warehouseLocations}/${row.id}`}
                className="text-blue-600 hover:underline font-medium"
            >
                {value as string}
            </Link>
        ),
    },
    {
        key: 'code',
        title: 'Code',
        width: 140,
        render: (value) => (
            <span className="text-sm font-mono">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'type',
        title: 'Type',
        width: 140,
        render: (_value, row) => {
            const variant =
                row.type === LOCATION_TYPE.DATACENTER ? 'secondary' : 'outline'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.type}
                </Badge>
            )
        },
    },
    {
        key: 'address',
        title: 'Address',
        width: 280,
        render: (value) => (
            <span className="text-sm truncate block max-w-[280px]">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'is_active',
        title: 'Status',
        width: 120,
        render: (_value, row) => (
            <Badge variant={row.is_active ? 'default' : 'destructive'}>
                {row.is_active ? 'Active' : 'Inactive'}
            </Badge>
        ),
    },
    {
        key: 'notes',
        title: 'Notes',
        width: 200,
        render: (value) => (
            <span className="text-sm truncate block max-w-[200px]">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'created_at',
        title: 'Created',
        width: 150,
        render: (value) => {
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.fromNow()}</span>
                </div>
            )
        },
    },
]

// === Filters ===

const locationTypeOptions = [
    { value: LOCATION_TYPE.WAREHOUSE, label: 'Warehouse' },
    { value: LOCATION_TYPE.DATACENTER, label: 'Datacenter' },
]

const locationStatusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
]

export const getLocationFilterItems = (): FilterItemConfig[] => [
    {
        type: 'select',
        name: 'type',
        label: 'Type',
        placeholder: 'Filter by type',
        options: locationTypeOptions,
    },
    {
        type: 'select',
        name: 'is_active',
        label: 'Status',
        placeholder: 'Filter by status',
        options: locationStatusOptions,
    },
]

// === Filter Toggles (column visibility) ===

export const getLocationFilterToggles = (): FilterToggle[] => [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    { key: 'type', label: 'Type' },
    { key: 'address', label: 'Address' },
    { key: 'is_active', label: 'Status' },
    { key: 'notes', label: 'Notes' },
    { key: 'created_at', label: 'Created' },
]
